import React, { Component } from "react";
import { connect } from "react-redux";
import {
  ChevronDown,
  ChevronRight,
  Pencil,
  Trash,
} from "react-bootstrap-icons";
import messages from "./TimelineView.messages";
import moment from "moment";

import * as actions from "../../../redux/timeline/actions";
import {
  getDefaultTimelineEvents,
  getTimelineEvents,
  isFetchingEvents,
} from "../../../redux/timeline";
import { getActiveProject } from "../../../redux/projects";
import { getActiveTab } from "../../../redux/app";
import { getUser, isLoggedIn } from "../../../redux/user";

import type User from "../../models/User";
import type Project from "../../models/Project";
import type TimelineEventCollection from "../../models/TimelineEventCollection";
import type TimelineEvent from "../../models/TimelineEvent";
import { accountExpired } from "../../../helpers/Utils";

import EventModal from "../components/modals/event-modal/EventModal";
import EventModalDelete from "../components/modals/event-modal-delete/EventModalDelete";
import GradientButton from "../../../components/input/GradientButton.jsx";

type OwnProps = {
  user: User,
};

type Props = OwnProps;

type StateProps = {
  activeTab: TabKey,
  activeProject: ?Project,
  isLoggedIn: boolean,
  isFetchingEvents: boolean,
  timelineEvents: TimelineEventCollection,
  defaultTimelineEvents: TimelineEventCollection,
};

type State = {
  showUpdateEventModal: boolean,
  showAddEventModal: boolean,
  showDeleteEventModal: boolean,
  selectedEvent: TimelineEvent,
};

const DATE_FORMAT = "MM-DD-YYYY";
const TIME_FORMAT = "h:mm a";
const DATE_TIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;
var date = null;
var day = null;
var eventCount = null;
var lastDay = null;

class TimelineView extends Component {
  props: Props;
  state: State = {
    showUpdateEventModal: false,
    showAddEventModal: false,
    showDeleteEventModal: false,
    selectedEvent: null,
  };
  loadProjectsPromise: ?Promise<void>;
  setActiveProjectPromise: ?Promise<void>;
  clearActiveProjectPromise: ?Promise<void>;

  constructor(props: Props) {
    super(props);
  }

  componentDidMount() {
    if (this.props.activeProject) {
      this.loadTimelineEventsPromise = Promise.resolve(
        this.props.loadTimelineEvents()
      );
    }
    if (this.props.user) {
      this.loadDefaultTimelineEventsPromise = Promise.resolve(
        this.props.loadDefaultTimelineEvents()
      );
    }
  }

  getSortedEvents(): TimelineEvent[] {
    const sortedEvents = this.props.timelineEvents.sortByDateTime();
    return sortedEvents.toArray();
  }

  openAddEventModal = () => {
    this.setState({
      showAddEventModal: true,
    });
  };

  closeAddEventModal = () => {
    this.setState({
      showAddEventModal: false,
    });
  };

  openUpdateEventModal = (event: TimelineEvent) => {
    this.setState({
      showUpdateEventModal: true,
      selectedEvent: event,
    });
  };

  closeUpdateEventModal = () => {
    this.setState({
      showUpdateEventModal: false,
      selectedEvent: null,
    });
  };

  openDeleteEventModal = (event: TimelineEvent) => {
    this.setState({
      showDeleteEventModal: true,
      selectedEvent: event,
    });
  };

  closeDeleteEventModal = () => {
    this.setState({
      showDeleteEventModal: false,
      selectedEvent: null,
    });
  };

  render() {
    return (
      <div className="timeline-container">
        {this.props.isFetchingEvents ? "" : this.renderEvents()}
        {this.state.showUpdateEventModal ? this.renderUpdateEventModal() : ""}
        {this.state.showAddEventModal ? this.renderAddEventModal() : ""}
        {this.state.showDeleteEventModal ? this.renderDeleteEventModal() : ""}
      </div>
    );
  }

  renderEvents() {
    if (!this.props.activeProject) return "";

    const project = this.props.activeProject;
    const userHasOwnerPrivileges =
      this.props.user && project && project.role === "owner";
    const events = this.getSortedEvents();
    eventCount = events.length;

    return (
      <div>
        <div className="timeline-title-container">
          <div className="project-title-container">
            <h1 className="project-title">{project.name}</h1>
          </div>
          {userHasOwnerPrivileges && !accountExpired(this.props.user)
            ? this.renderAddButton()
            : null}
        </div>
        {this.props.renderProjectNav()}
        <div className="events-list">
          {this.props.timelineEvents.length ? (
            events.map(this.renderEventRow)
          ) : (
            <div className="no-events-notice">No timeline items</div>
          )}
        </div>
      </div>
    );
  }

  renderAddButton() {
    return (
      <GradientButton title="+ Add Item" handleClick={this.openAddEventModal} />
    );
  }

  renderEventRow = (event: TimelineEvent, index: number) => {
    const userHasOwnerPrivileges =
      this.props.user &&
      this.props.activeProject &&
      this.props.activeProject.role === "owner";
    date = event.timezonelessDateTime();
    const dateTimeFormatted = moment.utc(date).format(DATE_TIME_FORMAT);
    day = moment.utc(dateTimeFormatted, DATE_TIME_FORMAT).format("M/D");
    const formattedTime = moment.utc(date).format("h:mm a");

    const isEventOpen = this.state.openEventIndex === index;

    const toggleEventOpen = () => {
      if (this.state.openEventIndex === index)
        this.setState({ openEventIndex: -1 });
      else this.setState({ openEventIndex: index });
    };

    //TODO combine this with the other calculate gradient
    //TODO make the lightest a little lighter and a little pinker
    // prettier-ignore
    const calculateGradient = (num) => {
      const ratio = (+num - 1) / +eventCount;
      return `rgb(${235 - 60 * ratio}, ${229 - 48 * ratio}, ${229 - 48 * ratio})`;
    };

    const renderDayHeader = () => {
      lastDay = day;
      return (
        <div className="event-header header-date">
          {moment.utc(date).format("dddd, MMMM Do, YYYY")}
        </div>
      );
    };

    return (
      <div key={event.id} className="event-row">
        {(!lastDay || day !== lastDay) && renderDayHeader()}
        <div>
          <div
            className="event-header event-name pointer"
            style={{ background: calculateGradient(index) }}
            onClick={toggleEventOpen}
          >
            <div className="event-time">
              <div className="time-alignment">{formattedTime}</div>
            </div>
            <div className="event-title deuppercase-children">
              {event.name ? event.name : messages.title}
            </div>
            <div className="event-expand-button">
              {isEventOpen ? (
                <ChevronDown size="20" />
              ) : (
                <ChevronRight size="20" />
              )}
            </div>
          </div>
          {isEventOpen && (
            <div className="event-info deuppercase-children">
              <div className="event-location">
                <b>Location</b>
              </div>
              <div className="event-location">{event.location}</div>
              <div className="event-details">
                <b>Details</b>
              </div>
              <div className="event-details">{event.details}</div>

              {userHasOwnerPrivileges && !accountExpired(this.props.user) && (
                <div className="owner-controls-container">
                  <div
                    className="owner-control pointer"
                    onClick={() => this.openUpdateEventModal(event)}
                  >
                    <Pencil size="25" />
                  </div>
                  <div
                    className="owner-control pointer"
                    onClick={() => this.openDeleteEventModal(event)}
                  >
                    <Trash size="25" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  renderUpdateEventModal = () => {
    return (
      <EventModal
        onModalClose={() => this.closeUpdateEventModal()}
        collapseEvent={() => this.setState({ openEventIndex: -1 })}
        user={this.props.user}
        project={this.props.activeProject}
        event={this.state.selectedEvent}
      ></EventModal>
    );
  };

  renderAddEventModal = () => {
    return (
      <EventModal
        onModalClose={() => this.closeAddEventModal()}
        user={this.props.user}
        project={this.props.activeProject}
        defaultTimelineEvents={this.props.defaultTimelineEvents}
      />
    );
  };

  renderDeleteEventModal = () => {
    return (
      <EventModalDelete
        onModalClose={() => this.closeDeleteEventModal()}
        user={this.props.user}
        project={this.props.activeProject}
        event={this.state.selectedEvent}
      ></EventModalDelete>
    );
  };
}

const mapStateToProps = (state) => {
  return {
    user: getUser(state),
    activeTab: getActiveTab(state),
    activeProject: getActiveProject(state),
    isLoggedIn: isLoggedIn(state),
    isFetchingEvents: isFetchingEvents(state),
    timelineEvents: getTimelineEvents(state),
    defaultTimelineEvents: getDefaultTimelineEvents(state),
  };
};

export default connect(mapStateToProps, actions)(TimelineView);



// WEBPACK FOOTER //
// ./src/views/user/timeline/TimelineView.js